import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import Spoiler from '../../../../../../../components/Spoiler'
import Field from '../../../../../../../components/Form/Field'
import LabelsSection from '../../../reusableFields/LabelsSection'
import RadioBlock from '../../../../../../../components/Form/RadioBlock'
import Step from '../../../../../../../features/components/Forms/StepForm/Step'
import MultipleGamMediaUpload from './MultipleGamMediaUpload'
import UploadedMediaIcon from './UploadedImageIcon'
import ForecastSection from './ForecastSection'
import UploadSummary from './UploadSummary'

import { gamRightUploadAdDataSelector } from '../../../../../../../modules/selectors/rights'
import { LINE_ITEM_TYPE, lineItemTypes } from '../../../LineItemForm/fields'

import {
  AD_TYPE_IMAGE,
  CLICK_THROUGH_LINK,
  CLICK_THROUGH_URL_OPTION,
  DIFFERENT_URL_TYPE,
  SAME_URL_TYPE,
  UPLOADED_MEDIA_LIST
} from '../../fields'
import { MEDIA_HEIGHT, MEDIA_WIDTH } from '../../../../../../ReusableFormFields/AdFileUpload/fields'

import useStyles from './styles'
import useDrawerFormStyles from '../../../../../../../styles/common/drawerForms'
import AdQuarantineField from '../../../../../../ReusableFormFields/AdQuarantineField'

function FileUploadStep({ formik, adType }) {
  const classes = useStyles()
  const { t } = useTranslation()
  const { values, setFieldValue } = formik

  const drawerFormClasses = useDrawerFormStyles()

  const { lineItem } = useSelector(gamRightUploadAdDataSelector)
  const lineItemSizes = lineItem?.creative_placeholders
  const isSponsorship = lineItem?.[LINE_ITEM_TYPE] === lineItemTypes.SPONSORSHIP

  const adSizes = useMemo(() => {
    if (lineItemSizes) {
      return lineItemSizes
        ?.slice()
        .sort((a, b) => a.size.width - b.size.width)
        .map(({ size }) => ({
          ...size,
          label: `${size.width}px x ${size.height}px`
        }))
    }
    return []
  }, [lineItemSizes])

  const stepTitle = useMemo(() => {
    if (adType === AD_TYPE_IMAGE) {
      return t('Upload image creatives')
    } else {
      return t('Upload video creatives')
    }
  }, [adType, t])

  return (
    <Step stepTitle={stepTitle}>
      <div className={drawerFormClasses.section}>
        <h3 className={drawerFormClasses.sectionTitle}>
          {t('Do you want to give the video creatives the same click through link?')}
        </h3>
        <RadioBlock
          setFieldValue={setFieldValue}
          id="radio_URL_same"
          name={CLICK_THROUGH_URL_OPTION}
          value={SAME_URL_TYPE}
          selectedValue={values[CLICK_THROUGH_URL_OPTION]}
          smallFontSize={true}
          label={t('Yes, give them all the same click through link')}
        >
          <Field formik={formik} name={CLICK_THROUGH_LINK} placeholder={t('Click through link')} autoComplete="off" />
        </RadioBlock>
        <RadioBlock
          setFieldValue={setFieldValue}
          id="radio_URL_different"
          smallFontSize={true}
          selectedValue={values[CLICK_THROUGH_URL_OPTION]}
          name={CLICK_THROUGH_URL_OPTION}
          value={DIFFERENT_URL_TYPE}
          label={t('No, give them different click through links')}
        />
      </div>

      <div className={drawerFormClasses.section}>
        <div>
          <h3>{t('Supported image sizes:')}</h3>
          <ul className={classes.supportedSizesList}>
            {adSizes?.map(size => {
              const wasUploaded = values[UPLOADED_MEDIA_LIST].some(media => {
                return media[MEDIA_WIDTH] === size.width && media[MEDIA_HEIGHT] === size.height
              })

              return (
                <li key={size.label} className={classes.sizeItem}>
                  <UploadedMediaIcon isSuccess={wasUploaded} />
                  {size.label}
                </li>
              )
            })}
          </ul>
        </div>

        {isSponsorship ? <UploadSummary formik={formik} /> : <ForecastSection formik={formik} />}
        <MultipleGamMediaUpload formik={formik} adType={adType} adSizes={adSizes} />
        <Spoiler title={t('Advanced Settings')} hasMarginLeft={false}>
          <LabelsSection
            formik={formik}
            description={t('Apply policy labels to this creative to set a frequency cap on this creative.')}
          />
        </Spoiler>
      </div>
      <AdQuarantineField formik={formik} />
    </Step>
  )
}

export default FileUploadStep
